import { ChevronDown, Plus, Trash2 } from 'lucide-react';
import { Input } from 'src/components/ui/input';
import { cn } from 'src/lib/utils';
import type { TemplateConstraint, TemplateRoleSpec } from '../types/creative-template.types';
import {
  CONSTRAINT_KIND_OPTIONS,
  CREATIVE_ROLE_OPTIONS,
  MIN_READINESS_OPTIONS,
} from '../types/creative-template.types';
import { EMPTY_CONSTRAINT, EMPTY_ROLE_SPEC } from '../helpers/creative-template.helpers';
import { SchedulerDsButton } from './scheduler-ds-button';

const labelClassName = 'text-sm font-medium text-gray-700';
const selectClassName = cn(
  'h-10 w-full appearance-none rounded border border-gray-400 bg-white pl-4 pr-10 text-base text-black',
  'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-black',
);

interface TemplateRoleSpecsEditorProps {
  values: TemplateRoleSpec[];
  onChange: (values: TemplateRoleSpec[]) => void;
}

function ConstraintEditor({
  constraint,
  onChange,
  onRemove,
}: {
  constraint: TemplateConstraint;
  onChange: (value: TemplateConstraint) => void;
  onRemove: () => void;
}) {
  return (
    <div className="grid gap-2 rounded border border-gray-200 bg-white p-2 md:grid-cols-2">
      <label className="relative block space-y-1">
        <span className="text-xs font-medium text-gray-600">Kind</span>
        <select
          value={constraint.kind}
          onChange={(event) => onChange({ ...constraint, kind: event.target.value as TemplateConstraint['kind'] })}
          className={selectClassName}
        >
          {CONSTRAINT_KIND_OPTIONS.map((kind) => (
            <option key={kind} value={kind}>{kind}</option>
          ))}
        </select>
      </label>
      {constraint.kind !== 'adjacent' ? (
        <label className="space-y-1">
          <span className="text-xs font-medium text-gray-600">Role</span>
          <Input
            value={constraint.role ?? ''}
            onChange={(event) => onChange({ ...constraint, role: event.target.value })}
          />
        </label>
      ) : (
        <>
          <label className="space-y-1">
            <span className="text-xs font-medium text-gray-600">Role A</span>
            <Input
              value={constraint.role_a ?? ''}
              onChange={(event) => onChange({ ...constraint, role_a: event.target.value })}
            />
          </label>
          <label className="space-y-1">
            <span className="text-xs font-medium text-gray-600">Role B</span>
            <Input
              value={constraint.role_b ?? ''}
              onChange={(event) => onChange({ ...constraint, role_b: event.target.value })}
            />
          </label>
        </>
      )}
      {constraint.kind === 'min_legible' ? (
        <label className="space-y-1">
          <span className="text-xs font-medium text-gray-600">Min px</span>
          <Input
            type="number"
            value={constraint.px ?? ''}
            onChange={(event) => onChange({ ...constraint, px: Number(event.target.value) })}
          />
        </label>
      ) : null}
      {constraint.kind === 'contrast_ratio' ? (
        <label className="space-y-1">
          <span className="text-xs font-medium text-gray-600">Min ratio</span>
          <Input
            type="number"
            step="0.1"
            value={constraint.min_ratio ?? ''}
            onChange={(event) => onChange({ ...constraint, min_ratio: Number(event.target.value) })}
          />
        </label>
      ) : null}
      {constraint.kind === 'safe_area' ? (
        <label className="space-y-1">
          <span className="text-xs font-medium text-gray-600">Margin frac</span>
          <Input
            type="number"
            step="0.01"
            value={constraint.margin_frac ?? ''}
            onChange={(event) => onChange({ ...constraint, margin_frac: Number(event.target.value) })}
          />
        </label>
      ) : null}
      <div className="md:col-span-2">
        <SchedulerDsButton type="button" variant="danger-ghost" size="sm" onClick={onRemove}>
          <Trash2 className="size-4" />
          Remove constraint
        </SchedulerDsButton>
      </div>
    </div>
  );
}

export function TemplateRoleSpecsEditor({ values, onChange }: TemplateRoleSpecsEditorProps) {
  const updateRow = (index: number, next: TemplateRoleSpec) => {
    onChange(values.map((value, itemIndex) => (itemIndex === index ? next : value)));
  };

  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-sm font-medium uppercase tracking-wide text-gray-500">Role specs</h3>
        <SchedulerDsButton
          type="button"
          variant="secondary"
          size="sm"
          onClick={() => onChange([...values, { ...EMPTY_ROLE_SPEC }])}
        >
          <Plus className="size-4" />
          Add role
        </SchedulerDsButton>
      </div>

      {values.map((row, index) => (
        <div key={`role-${index}`} className="space-y-3 rounded-md border border-gray-300 bg-gray-50 p-4">
          <div className="grid gap-3 md:grid-cols-2">
            <label className="relative block space-y-1">
              <span className={labelClassName}>Role</span>
              <select
                value={row.role}
                onChange={(event) => updateRow(index, { ...row, role: event.target.value as TemplateRoleSpec['role'] })}
                className={selectClassName}
              >
                {CREATIVE_ROLE_OPTIONS.map((role) => (
                  <option key={role} value={role}>{role}</option>
                ))}
              </select>
              <ChevronDown className="pointer-events-none absolute bottom-3 right-3 size-4 text-gray-500" />
            </label>

            <label className="relative block space-y-1">
              <span className={labelClassName}>Min readiness</span>
              <select
                value={row.min_readiness}
                onChange={(event) => updateRow(index, {
                  ...row,
                  min_readiness: event.target.value as TemplateRoleSpec['min_readiness'],
                })}
                className={selectClassName}
              >
                {MIN_READINESS_OPTIONS.map((option) => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
              <ChevronDown className="pointer-events-none absolute bottom-3 right-3 size-4 text-gray-500" />
            </label>

            <label className="space-y-1">
              <span className={labelClassName}>Drop rank</span>
              <Input
                type="number"
                value={row.drop_rank ?? ''}
                onChange={(event) => updateRow(index, {
                  ...row,
                  drop_rank: event.target.value === '' ? null : Number(event.target.value),
                })}
              />
            </label>

            <label className="flex items-center gap-2 pt-6">
              <input
                type="checkbox"
                checked={row.required}
                onChange={(event) => updateRow(index, { ...row, required: event.target.checked })}
                className="size-4 rounded border-gray-400"
              />
              <span className={labelClassName}>Required</span>
            </label>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className={labelClassName}>Constraints</span>
              <SchedulerDsButton
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => updateRow(index, {
                  ...row,
                  constraints: [...row.constraints, { ...EMPTY_CONSTRAINT }],
                })}
              >
                <Plus className="size-4" />
                Add constraint
              </SchedulerDsButton>
            </div>
            {row.constraints.map((constraint, constraintIndex) => (
              <ConstraintEditor
                key={`constraint-${index}-${constraintIndex}`}
                constraint={constraint}
                onChange={(next) => updateRow(index, {
                  ...row,
                  constraints: row.constraints.map((item, itemIndex) => (
                    itemIndex === constraintIndex ? next : item
                  )),
                })}
                onRemove={() => updateRow(index, {
                  ...row,
                  constraints: row.constraints.filter((_, itemIndex) => itemIndex !== constraintIndex),
                })}
              />
            ))}
          </div>

          <SchedulerDsButton
            type="button"
            variant="danger-ghost"
            size="sm"
            onClick={() => onChange(values.filter((_, itemIndex) => itemIndex !== index))}
          >
            <Trash2 className="size-4" />
            Remove role
          </SchedulerDsButton>
        </div>
      ))}
    </section>
  );
}
